/**
 * Rule Book API Service
 * Finance rules that every contract analysis is checked against
 */

import { apiConfig, makeApiRequest } from './apiConfig';
import { FinanceRuleSeverity } from '../../types/contract.types';

export const CONTRACT_TYPES = [
  'distribution', 'nda', 'service', 'employment', 'license', 'supply',
  'lease', 'partnership', 'franchise', 'consulting', 'saas', 'general'
] as const;

export type ContractTypeId = typeof CONTRACT_TYPES[number];

interface RuleUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface FinanceRule {
  _id: string;
  title: string;
  ruleText: string;
  severity: FinanceRuleSeverity;
  category: string;
  /** Empty means the rule applies to every contract type. */
  appliesTo: ContractTypeId[];
  suggestedPosition?: string;
  enabled: boolean;
  version: number;
  createdBy?: RuleUser;
  updatedBy?: RuleUser;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceRuleInput {
  title: string;
  ruleText: string;
  severity: FinanceRuleSeverity;
  category: string;
  appliesTo: ContractTypeId[];
  suggestedPosition?: string;
  /** Sent on update so a stale edit is rejected rather than overwriting someone else's. */
  version?: number;
}

export interface FinanceRuleRevision {
  _id: string;
  ruleId: string;
  version: number;
  change: 'created' | 'updated' | 'enabled' | 'disabled' | 'deleted';
  snapshot: Omit<FinanceRuleInput, 'version'> & { enabled: boolean };
  changedBy?: RuleUser;
  changedAt: string;
}

export type Classification = 'ACCEPTABLE' | 'NEGOTIATE' | 'HIGH_RISK' | 'CRITICAL';
export type PriorityTier = 1 | 2 | 3 | 4;

export interface ArgField {
  name: string;
  label: string;
  type: 'number' | 'classification' | 'optionMap';
  optional?: boolean;
  help?: string;
}

/** The parts of a playbook parameter finance can change. Absent fields keep the default. */
export interface PlaybookOverrideFields {
  enabled?: boolean;
  priorityTier?: PriorityTier;
  whenAbsent?: Classification;
  args?: Record<string, any>;
  preferredText?: string;
  fallbackText?: string;
  redFlagText?: string;
  challenge?: string;
}

export interface PlaybookParameterRow {
  id: string;
  topic: string;
  label: string;
  extraction: { kind: string; unit?: string; options?: string[] };
  contextOnly: boolean;
  evaluator: { kind: string; editable: boolean; fields: ArgField[] };
  defaults: Required<Omit<PlaybookOverrideFields, 'args'>> & { args: Record<string, any> };
  override: (PlaybookOverrideFields & { version: number; updatedBy?: RuleUser; updatedAt: string }) | null;
}

export interface PlaybookThresholds {
  id: string;
  name: string;
  version: string;
  perspective: string;
  appliesTo: string[];
  topics: { number: number; title: string; objective: string; parameterIds: string[] }[];
  classifications: Classification[];
  parameters: PlaybookParameterRow[];
}

export interface PlaybookSummary {
  id: string;
  name: string;
  version: string;
  appliesTo: string[];
  parameterCount: number;
}

export type RuleSuggestionStatus = 'pending' | 'accepted' | 'dismissed';

export interface RuleSuggestion {
  _id: string;
  title: string;
  ruleText: string;
  category: string;
  severity: FinanceRuleSeverity;
  appliesTo: ContractTypeId[];
  suggestedPosition?: string;
  rationale: string;
  status: RuleSuggestionStatus;
  /** Distinct contracts the problem was seen in. */
  contractCount: number;
  evidence: {
    contractKey: string;
    contractId?: string;
    fileName?: string;
    contractType?: string;
    clauseRef?: string;
    quote: string;
    observedAt: string;
  }[];
  lastSeenAt: string;
  acceptedRuleId?: string;
  reviewedBy?: RuleUser;
  reviewedAt?: string;
  dismissReason?: string;
  createdAt: string;
}

export type ImportRowAction = 'create' | 'update' | 'unchanged' | 'skip' | 'error';

export interface ImportPlanRow {
  line: number;
  action: ImportRowAction;
  title: string;
  errors: string[];
  note?: string;
}

export interface ImportResult {
  rows: ImportPlanRow[];
  counts: Record<ImportRowAction, number>;
  fileErrors: string[];
  applied: boolean;
  created?: number;
  updated?: number;
  failures?: { line: number; title: string; error: string }[];
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

class RuleService {
  async getAll(token: string): Promise<{ rules: FinanceRule[]; categories: string[] }> {
    const response = await makeApiRequest<FinanceRule[]>(
      apiConfig.endpoints.rules.getAll, { method: 'GET' }, token
    );
    return {
      rules: response.data || [],
      categories: (response as any).categories || []
    };
  }

  async create(token: string, input: FinanceRuleInput): Promise<FinanceRule> {
    const response = await makeApiRequest<FinanceRule>(
      apiConfig.endpoints.rules.create, { method: 'POST', ...json(input) }, token
    );
    return response.data as FinanceRule;
  }

  async update(token: string, id: string, input: FinanceRuleInput): Promise<FinanceRule> {
    const response = await makeApiRequest<FinanceRule>(
      apiConfig.endpoints.rules.update(id), { method: 'PUT', ...json(input) }, token
    );
    return response.data as FinanceRule;
  }

  async toggle(token: string, id: string, enabled: boolean): Promise<FinanceRule> {
    const response = await makeApiRequest<FinanceRule>(
      apiConfig.endpoints.rules.toggle(id), { method: 'PATCH', ...json({ enabled }) }, token
    );
    return response.data as FinanceRule;
  }

  async delete(token: string, id: string): Promise<void> {
    await makeApiRequest(apiConfig.endpoints.rules.delete(id), { method: 'DELETE' }, token);
  }

  /** Download the CSV template (a plain link can't carry the auth header). */
  async downloadTemplate(token: string): Promise<void> {
    const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.rules.importTemplate}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Couldn't download the template (${response.status})`);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = 'rule-book-template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  /** Preview (apply false) or run (apply true) a CSV import. */
  async importRules(token: string, csv: string, apply: boolean): Promise<ImportResult> {
    const response = await makeApiRequest<ImportResult>(
      apiConfig.endpoints.rules.import, { method: 'POST', ...json({ csv, apply }) }, token
    );
    return response.data as ImportResult;
  }

  async getSuggestions(token: string, status: RuleSuggestionStatus): Promise<{ suggestions: RuleSuggestion[]; pendingCount: number }> {
    const response = await makeApiRequest<RuleSuggestion[]>(
      `${apiConfig.endpoints.rules.suggestions}?status=${status}`, { method: 'GET' }, token
    );
    return { suggestions: response.data || [], pendingCount: (response as any).pendingCount ?? 0 };
  }

  async acceptSuggestion(token: string, id: string, input: FinanceRuleInput): Promise<void> {
    await makeApiRequest(apiConfig.endpoints.rules.suggestionAction(id, 'accept'), { method: 'POST', ...json(input) }, token);
  }

  async dismissSuggestion(token: string, id: string, reason?: string): Promise<void> {
    await makeApiRequest(apiConfig.endpoints.rules.suggestionAction(id, 'dismiss'), { method: 'POST', ...json({ reason }) }, token);
  }

  async restoreSuggestion(token: string, id: string): Promise<void> {
    await makeApiRequest(apiConfig.endpoints.rules.suggestionAction(id, 'restore'), { method: 'POST', ...json({}) }, token);
  }

  async getPlaybooks(token: string): Promise<PlaybookSummary[]> {
    const response = await makeApiRequest<PlaybookSummary[]>('/api/rules/playbooks', { method: 'GET' }, token);
    return response.data || [];
  }

  async getPlaybookThresholds(token: string, playbookId: string): Promise<PlaybookThresholds> {
    const response = await makeApiRequest<PlaybookThresholds>(
      apiConfig.endpoints.rules.playbook(playbookId), { method: 'GET' }, token
    );
    return response.data as PlaybookThresholds;
  }

  async saveOverride(
    token: string,
    playbookId: string,
    parameterId: string,
    fields: PlaybookOverrideFields & { version?: number }
  ): Promise<void> {
    await makeApiRequest(
      apiConfig.endpoints.rules.playbookParameter(playbookId, parameterId), { method: 'PUT', ...json(fields) }, token
    );
  }

  async resetOverride(token: string, playbookId: string, parameterId: string): Promise<void> {
    await makeApiRequest(
      apiConfig.endpoints.rules.playbookParameter(playbookId, parameterId), { method: 'DELETE' }, token
    );
  }

  async history(token: string, id: string): Promise<FinanceRuleRevision[]> {
    const response = await makeApiRequest<FinanceRuleRevision[]>(
      apiConfig.endpoints.rules.history(id), { method: 'GET' }, token
    );
    return response.data || [];
  }
}

export const ruleService = new RuleService();
export default ruleService;
